/**
 * Centralized tutorial cleanup utility
 * Ensures tutorial resources are deleted from backend AND React Query cache is invalidated
 *
 * Simplified approach: Always cleans up ALL tutorial resources for the current user.
 * No session ID tracking needed - just checks tutorial_session_id IS NOT NULL.
 */

import type { ApiClient } from '@markeroo/shared';
import type { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { clearTutorialState } from './types/window';

/**
 * Close any open modals by clearing URL parameters
 */
function closeModals(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const params = url.searchParams;

  // Close markup modal
  if (params.has('markupDocId')) {
    params.delete('markupDocId');
    window.history.replaceState({}, '', url.toString());
  }
}

export interface CleanupOptions {
  /** Whether to invalidate React Query caches (default: true) */
  invalidateCaches?: boolean;

  /** Whether to clear window state (default: true) */
  clearState?: boolean;

  /** Whether to close any open modals (default: false) */
  closeModals?: boolean;
}

/**
 * Cleanup ALL tutorial resources for the user (user-wide cleanup)
 * This should be called in ALL tutorial end scenarios to prevent orphaned resources
 */
export async function cleanupTutorialResources(
  api: ApiClient,
  queryClient: QueryClient,
  options: CleanupOptions = {}
): Promise<void> {
  const {
    invalidateCaches = true,
    clearState = true,
    closeModals: shouldCloseModals = false,
  } = options;

  try {
    // Close any open modals first (before backend cleanup)
    // This ensures modals close immediately when user skips tutorial
    if (shouldCloseModals) {
      closeModals();
      console.log('[TutorialCleanup] Modals closed');
    }

    // Cleanup ALL tutorial resources for this user (across all assignments)
    // Always run cleanup - no conditions needed
    try {
      await api.cleanupTutorialResources();
      console.log('[TutorialCleanup] Backend cleanup successful (user-wide)');
    } catch (apiError) {
      // Silently ignore 401 errors - user is not authenticated, so there's nothing to clean up
      // This happens when user visits site with expired session cookie but cached auth state
      if (isAxiosError(apiError) && apiError.response?.status === 401) {
        console.log('[TutorialCleanup] Skipping cleanup - user not authenticated (401)');
        return; // Exit early - no point invalidating caches if user isn't logged in
      }
      throw apiError; // Re-throw non-401 errors
    }

    // Invalidate React Query caches to remove tutorial resources from UI
    if (invalidateCaches) {
      const results = await Promise.allSettled([
        // Invalidate ALL assignment-documents caches (tutorial could affect any assignment)
        queryClient.invalidateQueries({ queryKey: ['assignment-documents'] }),

        // Invalidate ALL assignment caches (criteria counts, etc.)
        queryClient.invalidateQueries({ queryKey: ['assignment'] }),

        // Invalidate assignments list (counts, etc.)
        queryClient.invalidateQueries({ queryKey: ['assignments'] }),

        // Invalidate ALL student caches (tutorial could create students in any class)
        queryClient.invalidateQueries({ queryKey: ['class-students'] }),

        // User preferences (tutorial completion status)
        queryClient.invalidateQueries({ queryKey: ['current-user'] }),
      ]);

      const rejected = results.filter((r) => r.status === 'rejected');
      if (rejected.length > 0) {
        console.warn('[TutorialCleanup] Some cache invalidations failed:', rejected);
      }

      console.log('[TutorialCleanup] Cache invalidation successful');
    }

    // Only clear window state on SUCCESS - prevents state mismatch when cleanup fails
    // If cleanup fails, tutorial resources still exist in DB, so window flag should remain set
    // Next cleanup attempt (e.g., on page load) will retry
    if (clearState) {
      clearTutorialState();
      console.log('[TutorialCleanup] Window state cleared');
    }
  } catch (error) {
    console.error('[TutorialCleanup] Cleanup failed:', error);
    // Don't clear state on failure - let retry happen with window flag still set
    throw error;
  }
}

/**
 * Lightweight cleanup that doesn't throw - suitable for fire-and-forget scenarios
 * (component unmount, window unload, etc.)
 */
export async function cleanupTutorialResourcesSafe(
  api: ApiClient,
  queryClient: QueryClient,
  options: CleanupOptions = {}
): Promise<void> {
  try {
    await cleanupTutorialResources(api, queryClient, options);
  } catch (error) {
    console.warn('[TutorialCleanup] Safe cleanup failed:', error);
    // Swallow error - this is best-effort
  }
}
